import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useMetro } from '../context/MetroContext'
import {
  getAllHotels,
  formatDistance,
  hasCityNearbyData,
  calculateDistance,
} from '../services/nearbyPlaces'
import { NearbyPlace, Station } from '../types/metro.types'

interface NearbyStation {
  station: Station;
  distanceMeters: number;
}

function NearbyHotels() {
  const { selectedCity, currentCity, stations, isLoading } = useMetro()
  const [selectedHotelId, setSelectedHotelId] = useState('')

  // Get all hotels for the city
  const hotels = useMemo((): NearbyPlace[] => {
    if (!selectedCity || !hasCityNearbyData(selectedCity)) {
      return []
    }
    return getAllHotels(selectedCity)
  }, [selectedCity])

  // Get selected hotel
  const selectedHotel = useMemo(() => {
    return hotels.find(h => h.id === selectedHotelId)
  }, [hotels, selectedHotelId])

  // Get stations with coordinates
  const stationsWithCoords = useMemo(() => {
    return stations.filter(s => s.coordinates?.latitude && s.coordinates?.longitude)
  }, [stations])

  // Get nearby stations for selected hotel
  const nearbyStations = useMemo((): NearbyStation[] => {
    if (!selectedHotel || stationsWithCoords.length === 0) {
      return []
    }

    return stationsWithCoords
      .map(station => ({
        station,
        distanceMeters: calculateDistance(
          selectedHotel.latitude,
          selectedHotel.longitude,
          station.coordinates!.latitude,
          station.coordinates!.longitude
        ),
      }))
      .sort((a, b) => a.distanceMeters - b.distanceMeters)
      .slice(0, 5)
  }, [selectedHotel, stationsWithCoords])

  // Check if city has nearby data
  const hasData = selectedCity && hasCityNearbyData(selectedCity)

  if (!selectedCity) {
    return (
      <div className="py-8 lg:py-12">
        <div className="container-responsive">
          <div className="card text-center py-12">
            <span className="text-6xl mb-4 block">🏙️</span>
            <h2 className="text-xl font-semibold mb-2">Select a City First</h2>
            <p className="text-neutral-dark/70 mb-4">
              Please select a city to explore hotels near metro stations
            </p>
            <Link to="/cities" className="btn-primary inline-block">
              Select City
            </Link>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="py-8 lg:py-12">
        <div className="container-responsive">
          <div className="card text-center py-12">
            <div className="animate-spin w-12 h-12 border-4 border-metro-violet border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-neutral-dark/70">Loading city data...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="py-8 lg:py-12">
      <div className="container-responsive">
        <h1 className="text-3xl lg:text-4xl 2xl:text-tv-2xl font-bold text-center mb-4">
          Nearby Hotels
        </h1>
        <p className="text-center text-neutral-dark/70 mb-8">
          Find hotels and accommodations near metro stations in {currentCity?.name}
        </p>

        {!hasData && (
          <div className="card text-center py-8 mb-8 bg-metro-yellow-soft">
            <span className="text-4xl mb-2 block">⚠️</span>
            <p className="text-neutral-dark/70">
              Hotels data is not yet available for {currentCity?.name}.
              <br />Data available for: Delhi, Mumbai, Bangalore, Chennai, Kolkata, Hyderabad, Kochi, Lucknow, Jaipur, Nagpur, Ahmedabad
            </p>
          </div>
        )}

        {hasData && (
          <>
            {/* Hotel Selector */}
            <div className="card mb-8 max-w-xl mx-auto">
              <label className="block text-sm font-medium mb-2">Select Hotel</label>
              <select
                value={selectedHotelId}
                onChange={(e) => setSelectedHotelId(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-metro-violet focus:border-transparent"
              >
                <option value="">Choose a hotel to find nearest stations...</option>
                {hotels.map((hotel) => (
                  <option key={hotel.id} value={hotel.id}>
                    {hotel.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Nearby Stations */}
            {selectedHotel ? (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-center mb-4">
                  Nearest Metro Stations to {selectedHotel.name}
                </h2>
                {nearbyStations.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-4 max-w-5xl mx-auto">
                    {nearbyStations.map(({ station, distanceMeters }, index) => (
                      <div key={station.id} className={`card ${index === 0 ? 'ring-2 ring-metro-green bg-metro-green-soft/30' : ''}`}>
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                            index === 0 ? 'bg-metro-green' : 'bg-gray-400'
                          }`}>
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold truncate">{station.name}</h3>
                          </div>
                        </div>
                        <p className="text-xs text-neutral-dark/60 mb-2">
                          {station.lines.join(', ')}
                        </p>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-metro-violet font-medium">
                            📍 {formatDistance(distanceMeters)}
                          </span>
                          <span className="text-neutral-dark/50">
                            ~{Math.ceil(distanceMeters / 80)} min walk
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="card text-center py-8 text-neutral-dark/50 max-w-xl mx-auto">
                    <p>No stations with location data available</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="card text-center py-12 text-neutral-dark/50 mb-8">
                <span className="text-6xl mb-4 block">🚇</span>
                <p className="text-lg">Select a hotel to see nearby metro stations</p>
              </div>
            )}

            {/* All Hotels Grid */}
            <h2 className="text-xl font-semibold mb-4">All Hotels in {currentCity?.name}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6">
              {hotels.map((hotel) => (
                <div key={hotel.id} className="card">
                  <div className="aspect-video bg-metro-violet-soft rounded-lg mb-4 overflow-hidden">
                    {hotel.imageUrl ? (
                      <img
                        src={hotel.imageUrl}
                        alt={hotel.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-4xl">🏨</span>
                      </div>
                    )}
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{hotel.name}</h3>
                  <div className="flex items-center gap-1 mb-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span key={star} className="text-metro-yellow">★</span>
                    ))}
                    <span className="text-xs text-neutral-dark/50 ml-1">Premium</span>
                  </div>
                  {hotel.address && (
                    <p className="text-sm text-neutral-dark/70 mb-3">
                      {hotel.address}
                    </p>
                  )}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setSelectedHotelId(hotel.id)}
                      className="text-sm text-metro-violet hover:underline"
                    >
                      Find nearest stations
                    </button>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(hotel.name + ' ' + currentCity?.name)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-metro-violet hover:underline flex items-center gap-1"
                    >
                      🗺️ Maps
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default NearbyHotels
